// Region- + vintage-aware greenhouse-gas emissions for utility bills.
// Base factors: US EPA eGRID (electricity) + EPA GHG Emission Factors Hub
// (gas, fuels) and DCCEEW NGA (Australia). All expressed in kg CO2e per
// normalised base unit (kWh / therm / gallon) so the compute path is identical
// across regions — only the factor VALUES differ by region/subregion/vintage.

export const EPA_FACTORS = {
  electricity_kg_per_kwh: 0.373,   // eGRID US national avg (fallback default)
  natural_gas_kg_per_therm: 5.3,
  water_kg_per_gallon: 0.00038,
};

// Full active region sets from the DB store (populated by useEmissionFactors).
let _regionSets = [];

export function setEmissionFactors(f) {
  if (!f || typeof f !== 'object') return;
  if (f.electricity_kg_per_kwh != null) EPA_FACTORS.electricity_kg_per_kwh = Number(f.electricity_kg_per_kwh);
  if (f.natural_gas_kg_per_therm != null) EPA_FACTORS.natural_gas_kg_per_therm = Number(f.natural_gas_kg_per_therm);
  if (f.water_kg_per_gallon != null) EPA_FACTORS.water_kg_per_gallon = Number(f.water_kg_per_gallon);
}

// Store the full region×vintage sets + refresh the flat back-compat default.
export function setEmissionData({ factors, regions } = {}) {
  setEmissionFactors(factors);
  if (Array.isArray(regions)) _regionSets = regions;
}

// ───────────────────────── region resolution (mirrors backend) ─────────────
const US_STATES = new Set(['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']);
const AU_STATES = new Set(['NSW','ACT','VIC','QLD','SA','WA','TAS','NT']);
const UK_NATIONS = new Set(['ENG','SCT','WLS','NIR']);
const CA_PROVS = new Set(['BC','AB','SK','MB','ON','QC','NB','NS','PE','NL','YT','NT','NU']);
const EU_CODES = new Set(['AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE','NO','CH']);
const EU_AMBIGUOUS = new Set(['DE','MT','SK']); // collide with Delaware / Montana / Saskatchewan
const US_STATE_NAMES = { alabama:'AL', alaska:'AK', arizona:'AZ', arkansas:'AR', california:'CA', colorado:'CO', connecticut:'CT', delaware:'DE', 'district of columbia':'DC', florida:'FL', georgia:'GA', hawaii:'HI', idaho:'ID', illinois:'IL', indiana:'IN', iowa:'IA', kansas:'KS', kentucky:'KY', louisiana:'LA', maine:'ME', maryland:'MD', massachusetts:'MA', michigan:'MI', minnesota:'MN', mississippi:'MS', missouri:'MO', montana:'MT', nebraska:'NE', nevada:'NV', 'new hampshire':'NH', 'new jersey':'NJ', 'new mexico':'NM', 'new york':'NY', 'north carolina':'NC', 'north dakota':'ND', ohio:'OH', oklahoma:'OK', oregon:'OR', pennsylvania:'PA', 'rhode island':'RI', 'south carolina':'SC', 'south dakota':'SD', tennessee:'TN', texas:'TX', utah:'UT', vermont:'VT', virginia:'VA', washington:'WA', 'west virginia':'WV', wisconsin:'WI', wyoming:'WY' };
const AU_STATE_NAMES = { 'new south wales':'NSW', 'australian capital territory':'ACT', victoria:'VIC', queensland:'QLD', 'south australia':'SA', 'western australia':'WA', tasmania:'TAS', 'northern territory':'NT' };
const UK_STATE_NAMES = { england:'ENG', scotland:'SCT', wales:'WLS', cymru:'WLS', 'northern ireland':'NIR', 'n. ireland':'NIR' };
const CA_PROV_NAMES = { 'british columbia':'BC', alberta:'AB', saskatchewan:'SK', manitoba:'MB', ontario:'ON', quebec:'QC', 'québec':'QC', 'new brunswick':'NB', 'nova scotia':'NS', 'prince edward island':'PE', 'p.e.i.':'PE', 'newfoundland and labrador':'NL', newfoundland:'NL', labrador:'NL', yukon:'YT', 'northwest territories':'NT', nunavut:'NU' };
const EU_COUNTRY_NAMES = { austria:'AT', belgium:'BE', bulgaria:'BG', croatia:'HR', cyprus:'CY', czechia:'CZ', 'czech republic':'CZ', denmark:'DK', estonia:'EE', finland:'FI', france:'FR', germany:'DE', deutschland:'DE', greece:'GR', hellas:'GR', hungary:'HU', ireland:'IE', 'republic of ireland':'IE', italy:'IT', italia:'IT', latvia:'LV', lithuania:'LT', luxembourg:'LU', malta:'MT', netherlands:'NL', 'the netherlands':'NL', holland:'NL', poland:'PL', polska:'PL', portugal:'PT', romania:'RO', slovakia:'SK', 'slovak republic':'SK', slovenia:'SI', spain:'ES', 'españa':'ES', espana:'ES', sweden:'SE', norway:'NO', switzerland:'CH', suisse:'CH', schweiz:'CH' };
const EU_COUNTRY_ISO3 = { aut:'AT', bel:'BE', bgr:'BG', hrv:'HR', cyp:'CY', cze:'CZ', dnk:'DK', est:'EE', fin:'FI', fra:'FR', deu:'DE', grc:'GR', hun:'HU', irl:'IE', ita:'IT', lva:'LV', ltu:'LT', lux:'LU', mlt:'MT', nld:'NL', pol:'PL', prt:'PT', rou:'RO', svk:'SK', svn:'SI', esp:'ES', swe:'SE', nor:'NO', che:'CH' };
const US_COUNTRY = new Set(['us','usa','u.s.','u.s.a.','united states','united states of america','america']);
const AU_COUNTRY = new Set(['au','aus','australia','commonwealth of australia']);
const UK_COUNTRY = new Set(['uk','u.k.','gb','gbr','united kingdom','great britain','britain','england','scotland','wales','northern ireland']);
const CA_COUNTRY = new Set(['can','canada']);

function normCountry(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return null;
  if (US_COUNTRY.has(s)) return 'US';
  if (AU_COUNTRY.has(s)) return 'AU';
  if (UK_COUNTRY.has(s)) return 'UK';
  if (CA_COUNTRY.has(s)) return 'CA';
  const up = s.toUpperCase();
  if (EU_COUNTRY_NAMES[s] || EU_COUNTRY_ISO3[s]) return 'EU';
  if (EU_CODES.has(up) && !EU_AMBIGUOUS.has(up)) return 'EU';
  if (US_STATES.has(up) || US_STATE_NAMES[s]) return 'US';
  if (AU_STATES.has(up) || AU_STATE_NAMES[s]) return 'AU';
  if (UK_NATIONS.has(up) || UK_STATE_NAMES[s]) return 'UK';
  if (CA_PROVS.has(up) || CA_PROV_NAMES[s]) return 'CA';
  return null;
}
function normState(raw, region) {
  const s = String(raw || '').trim();
  if (!s) return null;
  const up = s.toUpperCase();
  const low = s.toLowerCase();
  if (region === 'AU') return AU_STATES.has(up) ? up : (AU_STATE_NAMES[low] || null);
  if (region === 'CA') return CA_PROVS.has(up) ? up : (CA_PROV_NAMES[low] || null);
  if (region === 'UK') return UK_NATIONS.has(up) ? up : (UK_STATE_NAMES[low] || null);
  if (region === 'EU') return EU_CODES.has(up) ? up : (EU_COUNTRY_NAMES[low] || EU_COUNTRY_ISO3[low] || null);
  return US_STATES.has(up) ? up : (US_STATE_NAMES[low] || null);
}
function euSubFromCountry(raw) {
  const s = String(raw || '').trim().toLowerCase();
  if (!s) return null;
  const up = s.toUpperCase();
  if (EU_CODES.has(up) && !EU_AMBIGUOUS.has(up)) return up;
  return EU_COUNTRY_NAMES[s] || EU_COUNTRY_ISO3[s] || null;
}
function ukSubFromCountry(raw) {
  return UK_STATE_NAMES[String(raw || '').trim().toLowerCase()] || null;
}
function regionFromState(raw) {
  const s = String(raw || '').trim();
  if (!s) return null;
  const up = s.toUpperCase();
  const low = s.toLowerCase();
  if (AU_STATES.has(up) || AU_STATE_NAMES[low]) return 'AU';
  if (US_STATES.has(up) || US_STATE_NAMES[low]) return 'US';
  if (CA_PROVS.has(up) || CA_PROV_NAMES[low]) return 'CA';
  if (UK_NATIONS.has(up) || UK_STATE_NAMES[low]) return 'UK';
  return null;
}
function regionFromPostcode(raw) {
  const s = String(raw || '').trim();
  if (/^[A-Za-z]{1,2}\d[A-Za-z\d]?\s*\d[A-Za-z]{2}$/.test(s)) return 'UK';   // UK postcode
  if (/^[A-Za-z]\d[A-Za-z]\s*\d[A-Za-z]\d$/.test(s)) return 'CA';            // Canadian postal code
  if (/^\d{4}$/.test(s)) return 'AU';            // Australian postcodes are 4 digits
  if (/^\d{5}(-\d{4})?$/.test(s)) return 'US';   // US ZIP / ZIP+4
  return null;
}
// Combined region resolution. 'WA' is ambiguous (US Washington vs AU Western
// Australia) — when that's the only state signal, let the postcode break the tie.
function resolveRegion({ country, state, postcode } = {}) {
  const byCountry = normCountry(country);
  if (byCountry) return byCountry;
  const byState = regionFromState(state);
  const byZip = regionFromPostcode(postcode);
  const up = String(state || '').trim().toUpperCase();
  if (up === 'WA' && byZip) return byZip;
  return byState || byZip || 'US';
}

// Resolve the effective per-commodity factors for a bill/property.
export function resolveRegionFactors({ country, state, postcode, year } = {}) {
  const resolved = resolveRegion({ country, state, postcode });
  if (!_regionSets.length) {
    return { factors: { ...EPA_FACTORS }, region: resolved, subregion: null, vintage_year: null, label: 'EPA (default)' };
  }
  let region = resolved;
  let docs = _regionSets.filter((d) => d.region === region);
  if (!docs.length) { region = 'US'; docs = _regionSets.filter((d) => d.region === 'US'); }
  if (!docs.length) { docs = _regionSets.slice(); region = docs[0]?.region || 'US'; }
  docs = [...docs].sort((a, b) => (a.vintage_year || 0) - (b.vintage_year || 0));
  let doc;
  if (year == null) doc = docs[docs.length - 1];
  else { const le = docs.filter((d) => (d.vintage_year || 0) <= year); doc = le.length ? le[le.length - 1] : docs[0]; }
  const factors = { ...(doc.factors || {}) };
  let sub = normState(state, region);
  if (!sub) {
    // EU/UK carry the subregion (country/nation) in the country field.
    if (region === 'EU') sub = euSubFromCountry(country);
    else if (region === 'UK') sub = ukSubFromCountry(country);
  }
  const subf = (doc.subregions || {})[sub || ''];
  if (subf) Object.keys(subf).forEach((k) => { if (typeof subf[k] === 'number') factors[k] = subf[k]; });
  // Guarantee the 3 core keys.
  if (factors.electricity_kg_per_kwh == null) factors.electricity_kg_per_kwh = EPA_FACTORS.electricity_kg_per_kwh;
  if (factors.natural_gas_kg_per_therm == null) factors.natural_gas_kg_per_therm = EPA_FACTORS.natural_gas_kg_per_therm;
  if (factors.water_kg_per_gallon == null) factors.water_kg_per_gallon = EPA_FACTORS.water_kg_per_gallon;
  return { factors, region, subregion: sub, subregion_code: subf?.subregion_code, vintage_year: doc.vintage_year, label: doc.label, resolved_by: subf ? 'state' : 'region' };
}

// EPA GHG Equivalencies (per kg CO2e)
const EQUIV = {
  miles_per_kg: 1 / 0.393,
  seedlings_per_kg: 1 / 60.3,
  gasoline_gal_per_kg: 1 / 8.887,
  smartphones_per_kg: 1 / 0.00822,
};

export function equivalenciesFromKg(totalKg = 0) {
  const kg = Number(totalKg) || 0;
  return {
    miles: kg * EQUIV.miles_per_kg,
    seedlings: kg * EQUIV.seedlings_per_kg,
    gasoline_gal: kg * EQUIV.gasoline_gal_per_kg,
    smartphones: kg * EQUIV.smartphones_per_kg,
  };
}

const num = (v) => {
  if (v == null) return 0;
  const n = parseFloat(String(v).replace(/[$,\s]/g, ''));
  return Number.isNaN(n) ? 0 : n;
};

const defaultUom = (commodity) => {
  const c = (commodity || '').toLowerCase();
  if (c.includes('gas')) return 'therms';
  if (c.includes('water') || c.includes('sewer')) return 'gallons';
  return 'kWh';
};

// Normalise a single {commodity, quantity, uom} line to kg CO2e using `factors`.
function lineToEmission(commodity, quantity, uom, factors = EPA_FACTORS) {
  const c = (commodity || '').toLowerCase();
  const u = (uom || '').toLowerCase();
  const q = num(quantity);
  if (q <= 0) return null;
  const fElec = factors.electricity_kg_per_kwh ?? EPA_FACTORS.electricity_kg_per_kwh;
  const fGas = factors.natural_gas_kg_per_therm ?? EPA_FACTORS.natural_gas_kg_per_therm;
  const fWater = factors.water_kg_per_gallon ?? EPA_FACTORS.water_kg_per_gallon;

  if (c.includes('elect')) {
    let kwh = q;
    if (u.includes('mwh')) kwh = q * 1000;
    return { type: 'Electricity', quantity: q, uom: uom || 'kWh',
      normQty: kwh, normUom: 'kWh', factor: fElec,
      factorLabel: `${fElec} kg/kWh`, kg: kwh * fElec };
  }
  if (c.includes('gas')) {
    let therms = q;
    if (u.includes('ccf')) therms = q * 1.037;
    else if (u.includes('mcf')) therms = q * 10.37;
    else if (u.includes('dth') || u.includes('dekatherm')) therms = q * 10;
    return { type: 'Natural Gas', quantity: q, uom: uom || 'therms',
      normQty: therms, normUom: 'therms', factor: fGas,
      factorLabel: `${fGas} kg/therm`, kg: therms * fGas };
  }
  if (c.includes('water') || c.includes('sewer')) {
    let gal = q;
    if (u.includes('ccf') || u.includes('hcf')) gal = q * 748.05;
    else if (u.includes('kgal')) gal = q * 1000;
    else if (u.includes('cgal')) gal = q * 100;
    return { type: c.includes('sewer') ? 'Sewer' : 'Water', quantity: q, uom: uom || 'gallons',
      normQty: gal, normUom: 'gallons', factor: fWater,
      factorLabel: `~${fWater} kg/gal (est.)`, kg: gal * fWater, estimate: true };
  }
  return null;
}

export function deriveUsageLines({ commodity, usage_details, meter_reads, accounts } = {}) {
  const lines = [];
  if (Array.isArray(meter_reads) && meter_reads.length) {
    for (const m of meter_reads) {
      const comm = m.commodity || commodity;
      const prev = num(m.previous_read);
      const curr = num(m.current_read);
      let mult = num(m.multiplier); if (!mult) mult = 1;
      let usage = num(m.usage);
      if (!usage && curr > prev) usage = (curr - prev) * mult;
      if (usage > 0) lines.push({ commodity: comm, quantity: usage, uom: m.uom || m.unit || defaultUom(comm) });
    }
    if (lines.length) return lines;
  }
  if (Array.isArray(accounts) && accounts.length) {
    for (const a of accounts) {
      const u = a.usage || {};
      const q = num(u.quantity ?? a.usage_quantity);
      if (q > 0) lines.push({ commodity: a.commodity || commodity, quantity: q, uom: u.unit || u.uom || defaultUom(a.commodity || commodity) });
    }
    if (lines.length) return lines;
  }
  if (Array.isArray(usage_details) && usage_details.length) {
    const byComm = {};
    for (const it of usage_details) {
      const comm = it.commodity || commodity || 'Electricity';
      const q = num(it.usage ?? it.usage_quantity ?? it.quantity);
      const uom = it.uom || it.unit || defaultUom(comm);
      if (q > 0 && (!byComm[comm] || q > byComm[comm].quantity)) {
        byComm[comm] = { commodity: comm, quantity: q, uom };
      }
    }
    return Object.values(byComm);
  }
  return [];
}

export function computeEmissions(lines, factors = EPA_FACTORS) {
  const raw = (lines || []).map((l) => lineToEmission(l.commodity, l.quantity, l.uom, factors)).filter(Boolean);
  const byType = {};
  for (const it of raw) {
    if (!byType[it.type]) byType[it.type] = { ...it };
    else {
      byType[it.type].normQty += it.normQty;
      byType[it.type].quantity += it.quantity;
      byType[it.type].kg += it.kg;
    }
  }
  const items = Object.values(byType);
  const total_kg = items.reduce((s, i) => s + i.kg, 0);
  const total_mt = total_kg / 1000;
  const hasEstimate = items.some((i) => i.estimate);
  return {
    hasData: items.length > 0 && total_kg > 0,
    items, total_kg, total_mt, hasEstimate,
    equivalencies: equivalenciesFromKg(total_kg),
  };
}

// Auto-resolves the bill's region (country/state/year) so single bills use the
// correct region + vintage factors instead of a flat US default.
export function emissionsFromParsed(data) {
  const lines = deriveUsageLines(data);
  const st = (data && data.statement) || {};
  const country = data?.country || st.service_country || st.country || st.primary_service_country;
  const state = data?.primary_service_state || data?.state || st.service_state || st.state || st.primary_service_state;
  const postcode = data?.postcode || data?.zip || st.postcode || st.postal_code || st.zip;
  const dateStr = st.invoice_date || st.statement_date || st.issue_date || data?.invoice_date || data?.issue_date || '';
  const ym = String(dateStr).match(/(20|21)\d{2}/);
  const year = ym ? Number(ym[0]) : null;
  const { factors, region, subregion, subregion_code, vintage_year, label, resolved_by } = resolveRegionFactors({ country, state, postcode, year });
  const e = computeEmissions(lines, factors);
  e.region = region; e.subregion = subregion; e.subregion_code = subregion_code;
  e.vintage_year = vintage_year; e.region_label = label; e.resolved_by = resolved_by;
  e.resolveInput = { country: country || '', state: state || '', postcode: postcode || '', year };
  return e;
}

// Rebuild an emissions result after overriding the electricity factor — used for
// server-side ZIP → eGRID subregion resolution (a state can span several
// subregions, so the bill's ZIP gives a more accurate electricity factor).
export function applyElectricityFactor(emissions, newFactor, meta = {}) {
  if (!emissions?.hasData || !(newFactor > 0)) return emissions;
  const items = emissions.items.map((it) => ({ ...it }));
  const el = items.find((it) => it.type === 'Electricity');
  if (!el || !(el.normQty > 0)) return emissions;
  el.factor = newFactor;
  el.factorLabel = `${newFactor} kg/kWh`;
  el.kg = el.normQty * newFactor;
  const total_kg = items.reduce((s, it) => s + (it.kg || 0), 0);
  return {
    ...emissions, items, total_kg, total_mt: total_kg / 1000,
    equivalencies: equivalenciesFromKg(total_kg),
    subregion: meta.subregion ?? emissions.subregion,
    subregion_code: meta.subregion_code ?? emissions.subregion_code,
    region_label: meta.label ?? emissions.region_label,
    vintage_year: meta.vintage_year ?? emissions.vintage_year,
    resolved_by: meta.resolved_by ?? emissions.resolved_by,
  };
}

// Dashboard/report helper — compute CO2e from pre-aggregated usage totals.
export function emissionsFromUsageTotals({ electricity_kwh, gas_therms, water_gallons } = {}, factors = EPA_FACTORS) {
  return computeEmissions([
    { commodity: 'Electricity', quantity: electricity_kwh, uom: 'kWh' },
    { commodity: 'Gas', quantity: gas_therms, uom: 'therms' },
    { commodity: 'Water', quantity: water_gallons, uom: 'gallons' },
  ], factors);
}

// CO2e (kg) for a single month row from the dashboard `series`.
export function monthlyCo2eKg(row = {}, factors = EPA_FACTORS) {
  const e = emissionsFromUsageTotals({
    electricity_kwh: row.electricity_kwh,
    gas_therms: row.gas_therms,
    water_gallons: row.water_gallons,
  }, factors);
  return e.total_kg;
}
